import { Component, EventEmitter, Output } from '@angular/core';
import { MenuItem, MessageService } from "primeng/api";
import { AuthService } from "@data/services/modules/auth/auth.service";
import { UserRole } from "@data/enums/user-role.enum";
import { environment } from "@env/environment";
import { BugReportLauncherService } from "@modules/private/bug-report/data/services/bug-report-launcher.service";
import { reportShortcutLabel } from "@data/utils/shortcut-label.util";

@Component({
  selector: 'app-skeleton-header',
  templateUrl: './skeleton-header.component.html',
  styleUrls: ['./skeleton-header.component.scss']
})
export class SkeletonHeaderComponent {
  avatar = 'assets/media/images/avatars/rendafin-R.png'
  items: MenuItem[] | undefined;
  role!: string | null;

  @Output() onChangeMenu = new EventEmitter()
  constructor(
    public authService: AuthService,
    private messageService: MessageService,
    private bugReport: BugReportLauncherService,
  ) { }

  ngOnInit() {
    const userId = this.authService.getUser?.user?.id;
    this.role = this.authService.getUser?.user?.permissions?.name ?? null;

    this.items = [
      //* Reportar un problema va PRIMERO: es la convención del panel para las acciones globales, y
      //* además es el único acceso que queda a mano si alguien no encuentra la lengüeta lateral.
      {
        label: `Reportar un problema · ${reportShortcutLabel()}`,
        icon: 'pi pi-fw pi-megaphone',
        command: () => this.bugReport.open('menu'),
      },
      { separator: true },
      ...(userId ? [
        {
          label: 'Copiar link de referidos',
          icon: 'pi pi-fw pi-share-alt',
          command: () => this.copyReferralLink(userId),
        },
        { separator: true },
      ] : []),
      {
        label: 'Cerrar sesión',
        icon: 'pi pi-fw pi-power-off',
        routerLink: 'logout'
      }
    ];
  }

  /**
   * Sistema de referidos: disponible para TODOS los usuarios internos del
   * panel (Admin, Operaciones, Finanzas, Broker, Seller). El backend valida
   * que el `referredBy` no sea un Client y, si OK, atribuye el cliente nuevo
   * al referrer en `client.referredBy` (+ `client.seller` si estaba vacío).
   */
  private copyReferralLink(userId: string): void {
    // ADR-0031: el link de referido apunta DIRECTO a /contact-verification (registro:
    // elige PF/PM + captura datos + SMS OTP), no al cotizador. Antes iba a /quotation
    // porque la CIEC estaba acoplada al lease y saltarse el cotizador creaba un lease con
    // provitionalValue=0 (bug silencioso). ADR-0031 desacopló la CIEC del arrendamiento
    // (entidad ClientVerification a nivel cliente), así que el referido —cliente "tibio"
    // identificado— ya no necesita cotizar primero. Alinea el panel con digital-channel
    // (InternalDashboard), que ya usa este entry point.
    const link = `${environment.clientAppUrl}/contact-verification?ref=${userId}`;
    navigator.clipboard.writeText(link).then(
      () => this.messageService.add({
        severity: 'success',
        summary: 'Copiado',
        detail: 'Link de referidos copiado al portapapeles',
        life: 3000,
      }),
      () => this.messageService.add({
        severity: 'error',
        summary: 'No se pudo copiar',
        detail: 'Intenta de nuevo o copia el link manualmente desde la consola',
        life: 4000,
      }),
    );
  }

  getRoleClass(): string {
    switch (this.role) {
      case UserRole.ADMIN:
        return 'chip-admin';
      case UserRole.OPERACIONES:
        return 'chip-operaciones';
      case UserRole.FINANZAS:
        return 'chip-finanzas';
      case UserRole.SELLER:
        return 'chip-seller';
      case UserRole.BROKER:
        return 'chip-broker';
      default:
        return 'chip-default';
    }
  }
}
